import { useState, useEffect } from 'react';
import type { FC, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminFetch } from '../utils/api';
import { formatCents, formatDate } from '../utils/format';
import DateRangePicker from '../components/DateRangePicker';

interface UserAccount {
  cognitoUserId: string;
  email: string;
}

interface Transaction {
  transactionId: string;
  type: string;
  amountCents: number;
  description: string;
  createdAt: string;
}

const TYPE_BADGES: Record<string, string> = {
  deposit: 'bg-success',
  'welcome-credit': 'bg-info',
  'article-generation': 'bg-primary',
  'article-import': 'bg-primary',
  'custom-translation': 'bg-secondary',
  adjustment: 'bg-warning text-dark',
  refund: 'bg-danger',
};

const TYPE_LABELS: Record<string, string> = {
  'article-generation': 'article',
  'article-import': 'article',
};

const Transactions: FC = () => {
  const { token } = useAuth();

  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<UserAccount[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [txnLoading, setTxnLoading] = useState(false);
  const [txnError, setTxnError] = useState('');
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    if (!token || !query.trim()) return;
    setSearching(true);
    setSearchError('');
    setMatches([]);
    setSelectedUserId('');
    setTransactions([]);
    try {
      const data = await adminFetch<{ users: UserAccount[] }>(
        `/admin/users/search?q=${encodeURIComponent(query.trim())}`,
        token,
      );
      setMatches(data.users);
      if (data.users.length === 1) setSelectedUserId(data.users[0].cognitoUserId);
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const fetchTransactions = (fromISO: string, toISO: string) => {
    if (!token || !selectedUserId) return;
    setRange({ from: fromISO, to: toISO });
    setTxnLoading(true);
    setTxnError('');
    adminFetch<{ transactions: Transaction[] }>(
      `/admin/users/${selectedUserId}/transactions?limit=500&from=${encodeURIComponent(fromISO)}&to=${encodeURIComponent(toISO)}`,
      token,
    )
      .then(data => setTransactions(data.transactions))
      .catch(err => setTxnError(err instanceof Error ? err.message : 'Failed to load transactions'))
      .finally(() => setTxnLoading(false));
  };

  // When the user picks a different account from the dropdown, drop any
  // stale rows but don't auto-fetch — the admin chooses the date range.
  useEffect(() => {
    setTransactions([]);
    setRange(null);
  }, [selectedUserId]);

  const net = transactions.reduce((s, t) => s + t.amountCents, 0);

  return (
    <div className="p-3">
      <form onSubmit={handleSearch} className="d-flex align-items-center gap-2 mb-3">
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Email prefix..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          autoFocus
          style={{ maxWidth: 300 }}
        />
        <button type="submit" className="btn btn-primary btn-sm" disabled={searching || !query.trim()}>
          {searching ? '...' : 'Search'}
        </button>
        {matches.length > 0 && (
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 400 }}
            value={selectedUserId}
            onChange={e => setSelectedUserId(e.target.value)}
          >
            <option value="">Select user ({matches.length} match{matches.length === 1 ? '' : 'es'})...</option>
            {matches.map(u => (
              <option key={u.cognitoUserId} value={u.cognitoUserId}>{u.email}</option>
            ))}
          </select>
        )}
      </form>

      {searchError && <div className="alert alert-warning py-2 small">{searchError}</div>}

      {selectedUserId && (
        <>
          <DateRangePicker onFetch={fetchTransactions} loading={txnLoading}>
            {range && transactions.length > 0 && (
              <span className={`ms-auto fw-bold ${net >= 0 ? 'text-success' : 'text-danger'}`}>
                Net: {formatCents(net)}
              </span>
            )}
          </DateRangePicker>

          {txnError && <div className="alert alert-warning py-2 small">{txnError}</div>}

          {range && (
            <table className="table table-striped table-sm align-middle">
              <thead className="table-dark">
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th className="text-end">Amount</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(txn => (
                  <tr key={txn.transactionId}>
                    <td className="small">{formatDate(txn.createdAt)}</td>
                    <td>
                      <span className={`badge ${TYPE_BADGES[txn.type] || 'bg-secondary'}`}>
                        {TYPE_LABELS[txn.type] || txn.type}
                      </span>
                    </td>
                    <td className={`text-end fw-bold ${txn.amountCents >= 0 ? 'text-success' : 'text-danger'}`}>
                      {txn.amountCents >= 0 ? '+' : ''}{formatCents(txn.amountCents)}
                    </td>
                    <td className="small">{txn.description}</td>
                  </tr>
                ))}
                {transactions.length === 0 && !txnLoading && (
                  <tr><td colSpan={4} className="text-center text-muted">No transactions in this period</td></tr>
                )}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

export default Transactions;
